/**
 * QMS Shipping Section
 * Collapsible table for managing shipping records on client tickets
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, Plus, Pencil, Trash2, X, Check } from 'lucide-react';
import { toast } from 'sonner';
import type { QmsShipping } from '../../../types/qms.types';
import { useUpsertShipping, useDeleteShipping } from '../../../hooks/useQmsSubResources';

interface QmsShippingSectionProps {
  ticketId: string;
  shipping: QmsShipping[];
}

const emptyShipping: Partial<QmsShipping> = {
  provider: '',
  trackingNumber: '',
  status: '',
  shippingDate: '',
};

export function QmsShippingSection({ ticketId, shipping }: QmsShippingSectionProps) {
  const { t } = useTranslation('qms');
  const [expanded, setExpanded] = useState(shipping.length > 0);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editData, setEditData] = useState<Partial<QmsShipping>>({});
  const [isAdding, setIsAdding] = useState(false);
  const [newItem, setNewItem] = useState<Partial<QmsShipping>>(emptyShipping);

  const upsertMutation = useUpsertShipping(ticketId);
  const deleteMutation = useDeleteShipping(ticketId);

  const handleAdd = async () => {
    try {
      await upsertMutation.mutateAsync(newItem);
      setIsAdding(false);
      setNewItem(emptyShipping);
      toast.success(t('subResources.shipping.added', 'Shipping record added'));
    } catch {
      toast.error(t('subResources.shipping.addError', 'Failed to add shipping record'));
    }
  };

  const handleUpdate = async () => {
    if (!editingId) return;
    try {
      await upsertMutation.mutateAsync({ ...editData, id: editingId });
      setEditingId(null);
      setEditData({});
      toast.success(t('subResources.shipping.updated', 'Shipping record updated'));
    } catch {
      toast.error(t('subResources.shipping.updateError', 'Failed to update shipping record'));
    }
  };

  const handleDelete = async (shippingId: string) => {
    if (!confirm(t('subResources.confirmDelete', 'Delete this item?'))) return;
    try {
      await deleteMutation.mutateAsync(shippingId);
      toast.success(t('subResources.shipping.deleted', 'Shipping record deleted'));
    } catch {
      toast.error(t('subResources.shipping.deleteError', 'Failed to delete shipping record'));
    }
  };

  const startEditing = (ship: QmsShipping) => {
    setEditingId(ship.id);
    setEditData({ provider: ship.provider, trackingNumber: ship.trackingNumber, status: ship.status, shippingDate: ship.shippingDate, deliveryDate: ship.deliveryDate });
  };

  return (
    <div className="border-t pt-4">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 text-base font-semibold text-gray-900 hover:text-atek-blue"
      >
        {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        {t('form.sections.shipping', 'Shipping')}
        <span className="text-sm font-normal text-gray-500">({shipping.length})</span>
      </button>

      {expanded && (
        <div className="mt-3">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left">
                  <th className="px-3 py-2 font-medium text-gray-600">{t('form.fields.provider', 'Provider')}</th>
                  <th className="px-3 py-2 font-medium text-gray-600">{t('form.fields.trackingNumber', 'Tracking #')}</th>
                  <th className="px-3 py-2 font-medium text-gray-600">{t('form.fields.status', 'Status')}</th>
                  <th className="px-3 py-2 font-medium text-gray-600">{t('form.fields.shippingDate', 'Ship Date')}</th>
                  <th className="px-3 py-2 font-medium text-gray-600">{t('form.fields.deliveryDate', 'Delivery Date')}</th>
                  <th className="px-3 py-2 font-medium text-gray-600 w-20"></th>
                </tr>
              </thead>
              <tbody>
                {shipping.map((ship) => (
                  editingId === ship.id ? (
                    <tr key={ship.id} className="border-b border-gray-100 bg-blue-50">
                      <td className="px-2 py-1"><input type="text" value={editData.provider || ''} onChange={(e) => setEditData({ ...editData, provider: e.target.value })} className="w-full px-2 py-1 border rounded text-sm" /></td>
                      <td className="px-2 py-1"><input type="text" value={editData.trackingNumber || ''} onChange={(e) => setEditData({ ...editData, trackingNumber: e.target.value })} className="w-full px-2 py-1 border rounded text-sm" /></td>
                      <td className="px-2 py-1"><input type="text" value={editData.status || ''} onChange={(e) => setEditData({ ...editData, status: e.target.value })} className="w-full px-2 py-1 border rounded text-sm" /></td>
                      <td className="px-2 py-1"><input type="date" value={editData.shippingDate || ''} onChange={(e) => setEditData({ ...editData, shippingDate: e.target.value })} className="w-full px-2 py-1 border rounded text-sm" /></td>
                      <td className="px-2 py-1"><input type="date" value={editData.deliveryDate || ''} onChange={(e) => setEditData({ ...editData, deliveryDate: e.target.value })} className="w-full px-2 py-1 border rounded text-sm" /></td>
                      <td className="px-2 py-1 flex gap-1">
                        <button type="button" onClick={handleUpdate} disabled={upsertMutation.isPending} className="p-1 text-green-600 hover:bg-green-100 rounded"><Check className="h-4 w-4" /></button>
                        <button type="button" onClick={() => setEditingId(null)} className="p-1 text-gray-500 hover:bg-gray-100 rounded"><X className="h-4 w-4" /></button>
                      </td>
                    </tr>
                  ) : (
                    <tr key={ship.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="px-3 py-2 text-gray-900">{ship.provider || '-'}</td>
                      <td className="px-3 py-2 text-gray-900 font-mono">{ship.trackingNumber || '-'}</td>
                      <td className="px-3 py-2 text-gray-900">{ship.status || '-'}</td>
                      <td className="px-3 py-2 text-gray-900">{ship.shippingDate || '-'}</td>
                      <td className="px-3 py-2 text-gray-900">{ship.deliveryDate || '-'}</td>
                      <td className="px-3 py-2 flex gap-1">
                        <button type="button" onClick={() => startEditing(ship)} className="p-1 text-gray-500 hover:bg-gray-100 rounded"><Pencil className="h-3.5 w-3.5" /></button>
                        <button type="button" onClick={() => handleDelete(ship.id)} disabled={deleteMutation.isPending} className="p-1 text-red-500 hover:bg-red-100 rounded"><Trash2 className="h-3.5 w-3.5" /></button>
                      </td>
                    </tr>
                  )
                ))}
                {isAdding && (
                  <tr className="border-b border-gray-100 bg-green-50">
                    <td className="px-2 py-1"><input type="text" value={newItem.provider || ''} onChange={(e) => setNewItem({ ...newItem, provider: e.target.value })} className="w-full px-2 py-1 border rounded text-sm" placeholder="Provider" /></td>
                    <td className="px-2 py-1"><input type="text" value={newItem.trackingNumber || ''} onChange={(e) => setNewItem({ ...newItem, trackingNumber: e.target.value })} className="w-full px-2 py-1 border rounded text-sm" placeholder="Tracking #" /></td>
                    <td className="px-2 py-1"><input type="text" value={newItem.status || ''} onChange={(e) => setNewItem({ ...newItem, status: e.target.value })} className="w-full px-2 py-1 border rounded text-sm" placeholder="Status" /></td>
                    <td className="px-2 py-1"><input type="date" value={newItem.shippingDate || ''} onChange={(e) => setNewItem({ ...newItem, shippingDate: e.target.value })} className="w-full px-2 py-1 border rounded text-sm" /></td>
                    <td className="px-2 py-1"><input type="date" value={newItem.deliveryDate || ''} onChange={(e) => setNewItem({ ...newItem, deliveryDate: e.target.value })} className="w-full px-2 py-1 border rounded text-sm" /></td>
                    <td className="px-2 py-1 flex gap-1">
                      <button type="button" onClick={handleAdd} disabled={upsertMutation.isPending} className="p-1 text-green-600 hover:bg-green-100 rounded"><Check className="h-4 w-4" /></button>
                      <button type="button" onClick={() => setIsAdding(false)} className="p-1 text-gray-500 hover:bg-gray-100 rounded"><X className="h-4 w-4" /></button>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {!isAdding && (
            <button type="button" onClick={() => setIsAdding(true)} className="mt-2 flex items-center gap-1 text-sm text-atek-blue hover:text-atek-blue-dark">
              <Plus className="h-4 w-4" /> {t('subResources.shipping.add', 'Add Shipping')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
